import { useState } from "react";
import { z } from "zod";
import { useStore } from "../../app/services.js";
import { parseJson } from "../../lib/storage.js";

export const OPTIONAL_STEPS = ["forward", "test", "action", "domain"] as const;
export type OptionalStep = (typeof OPTIONAL_STEPS)[number];

const skippedSchema = z.array(z.enum(OPTIONAL_STEPS));

function storageKey(tenantId: string): string {
  return `hooky.setup.skipped.${tenantId}`;
}

/** A stored value that does not parse counts as no skips. */
function parseSkipped(raw: string | null): ReadonlySet<OptionalStep> {
  if (raw === null) return new Set();
  const parsed = skippedSchema.safeParse(parseJson(raw));
  return new Set(parsed.success ? parsed.data : []);
}

/** The optional setup steps this browser skipped for the tenant. */
export function useSkippedSteps(tenantId: string): { skipped: ReadonlySet<OptionalStep>; skip: (step: OptionalStep) => void } {
  const store = useStore();
  const [skipped, setSkipped] = useState(() => parseSkipped(store.getItem(storageKey(tenantId))));

  function skip(step: OptionalStep): void {
    const next = new Set(skipped).add(step);
    store.setItem(storageKey(tenantId), JSON.stringify([...next]));
    setSkipped(next);
  }

  return { skipped, skip };
}
